// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The frame-time governor (M3-36, ARCH-012, 08 §6).
//
// "Steps the tier down if measured frame time exceeds budget for 3 seconds, with a discreet
// notice. **The 1000 Hz simulation rate never changes** — the player may get a less pretty
// machine but never a different one."
//
// That last sentence is the whole design constraint, and it is honoured by construction rather
// than by care: this class is given frame times and a tier name. It has no driver, no step
// count, no world and no way to reach one. There is no line to review, because there is no
// line it *could* write. A leaderboard score is validated by re-simulating the input trace
// server-side, so a client that slowed its own physics under load would produce scores that
// fail validation on the machine of every player whose laptop got warm.
//
// **What is actually hard here is not the three seconds. It is deciding what counts as late.**
//
// The naive rule — "frame interval over `frameBudgetMs`" — is wrong in both directions:
//
//   * A 60 Hz display produces intervals that straddle 16.7 ms by a fraction of a millisecond
//     forever. Against a 16.7 ms line, a perfectly healthy machine is over budget about half
//     the time, and any run of three seconds of jitter steps it down.
//   * A **30 Hz display** — a power-saving laptop, a phone in low-power mode, a browser
//     throttling an unfocused tab — produces 33 ms intervals no matter how little work the GPU
//     is given. Against the same line it is permanently over budget, so the governor would walk
//     it from `high` to `low` in six seconds and leave it there, having improved nothing at
//     all. The machine would simply be uglier.
//
// So the governor learns the display's own cadence and measures against **the slower of the
// budget and the cadence**, plus a margin. The budget is a target; the refresh rate is a fact,
// and a device cannot be blamed for a frame it was never offered the chance to draw sooner.
//
// The cadence is the *median* of the intervals just after a settle — median rather than
// minimum, because request-animation-frame coalescing hands out the occasional 2 ms interval
// that a minimum would take for the refresh rate — and it is **clamped**, because a device that
// was already overloaded while the cadence was being learned would otherwise teach the governor
// that 80 ms is normal here and never trip again. No display refreshes slower than about 30 Hz;
// anything slower than that is not a cadence, it is the thing this exists to catch.

import { TIER_BUDGETS, tierBelow, type RenderTier } from './tiers'

/** How long the frames have to stay late before anything happens. 08 §6's three seconds. */
export const SUSTAINED_SECONDS = 3

/**
 * How far past the line a frame has to be to count as late.
 *
 * A quarter. At a 16.7 ms budget that is 20.8 ms, which a 60 Hz display reaches only by
 * genuinely missing a refresh — the next stop above it is 33 ms. Tighter than this and vsync
 * jitter trips the governor; looser and a device running at 40 fps is told it is fine.
 */
export const OVERRUN_FACTOR = 1.25

/**
 * Longer than this and the frame was not slow, it was absent.
 *
 * A backgrounded tab, a modal print dialog, an alert, a laptop lid, a machine being rebuilt on
 * `R`, a garbage collection over a freshly loaded machine. Counting those as overload steps the
 * tier down for something the GPU never did, and the player comes back from another tab to a
 * worse-looking machine. Not counted in either direction: a stall is not evidence of health
 * either, so it does not reset the run.
 */
export const STALL_MS = 250

/** Frames to ignore after a tier change or a rebuild, while shaders compile and textures upload. */
export const SETTLE_FRAMES = 20

/** Intervals used to learn the display cadence, after the settle. About a second at 60 Hz. */
export const CADENCE_FRAMES = 45

/**
 * The slowest interval that can be believed as a refresh rate, ms.
 *
 * 30 Hz is 33.3 ms and is a real display mode; 34 leaves it a whisker of room. Anything slower
 * is a device in trouble, and letting it set the cadence would be letting it excuse itself.
 */
export const MAX_CADENCE_MS = 34

/** What happened, for the discreet notice and for the HUD. */
export interface TierStep {
  readonly from: RenderTier
  readonly to: RenderTier
  /** The trip line that was being exceeded, ms. */
  readonly lateMs: number
  /** One sentence, for a player rather than for a log. */
  readonly notice: string
}

/**
 * Watches frame times and steps the render tier down. Never up, and never anything else.
 *
 * Not stepping back up is deliberate. The condition that caused the step — a hot laptop, a
 * video call in another tab, multiball with every flasher going — usually persists, and a
 * governor with both directions oscillates: it steps down, the frames recover *because* it
 * stepped down, it steps back up, and the machine visibly changes fidelity every six seconds
 * for the rest of the game. One quiet step down is a machine that stays smooth. Going back up
 * is the settings panel's job, where a person asks for it.
 */
export class FrameGovernor {
  private tierName: RenderTier
  private settleLeft = SETTLE_FRAMES
  private cadenceSamples: number[] = []
  private cadence = 0
  private lateSeconds = 0
  private stepsTaken = 0
  private lastStep: TierStep | undefined
  private atFloor = false

  constructor(tier: RenderTier) {
    this.tierName = tier
  }

  get tier(): RenderTier {
    return this.tierName
  }

  /** The display's own interval, ms, once learned. 0 while still learning. */
  get cadenceMs(): number {
    return this.cadence
  }

  /** How long the frames have been late, seconds. Resets on the first frame that is not. */
  get lateFor(): number {
    return this.lateSeconds
  }

  /** The trip line in force, ms. 0 while the cadence is still being learned. */
  get lateAboveMs(): number {
    if (this.cadence <= 0) return 0
    return Math.max(TIER_BUDGETS[this.tierName].frameBudgetMs, this.cadence) * OVERRUN_FACTOR
  }

  /** How many times it has stepped this session. */
  get steps(): number {
    return this.stepsTaken
  }

  /** The most recent step, for the notice. */
  get lastNotice(): TierStep | undefined {
    return this.lastStep
  }

  /**
   * Still late at the lowest tier there is.
   *
   * Worth reporting and not worth acting on: there is nothing below `low`, and refusing to draw
   * is not an improvement. It is the honest answer to "why is this still stuttering" and the
   * signal that a device has been asked for more than it has.
   */
  get struggling(): boolean {
    return this.atFloor
  }

  /**
   * Offer one frame's wall-clock interval. Returns the new tier if this frame caused a step.
   *
   * The **interval**, not the time spent inside `render()`. What a player experiences is how
   * often the picture changes, and CPU time inside a draw call measures neither the GPU's work
   * — which is still queued when the call returns — nor anything else on the frame. The
   * interval is the only number that has all of it in it.
   */
  sample(frameMs: number): RenderTier | undefined {
    if (!Number.isFinite(frameMs) || frameMs <= 0) return undefined
    // A stall is neither evidence of overload nor evidence of health. It also must not be
    // allowed near the cadence median, where one lid-close would set the trip line at its cap.
    if (frameMs > STALL_MS) return undefined

    if (this.settleLeft > 0) {
      this.settleLeft--
      return undefined
    }

    if (this.cadenceSamples.length < CADENCE_FRAMES) {
      this.cadenceSamples.push(frameMs)
      if (this.cadenceSamples.length === CADENCE_FRAMES) this.cadence = this.learnCadence()
      // Nothing trips while the line is still unknown. That is at most a second, and a governor
      // that guessed during it would be guessing at exactly the moment a machine is loading.
      return undefined
    }

    const line = this.lateAboveMs
    if (frameMs <= line) {
      this.lateSeconds = 0
      return undefined
    }

    this.lateSeconds += frameMs / 1000
    if (this.lateSeconds < SUSTAINED_SECONDS) return undefined

    const next = tierBelow(this.tierName)
    if (next === undefined) {
      // `low`, or `xr` — which is a device rather than a rung and has nothing below it. Stop
      // accumulating so the flag means "late now", not "was late once".
      this.atFloor = true
      this.lateSeconds = 0
      return undefined
    }

    const step: TierStep = {
      from: this.tierName,
      to: next,
      lateMs: line,
      notice: `Graphics stepped down to ${next} to keep the machine smooth. Change it in settings.`,
    }
    this.tierName = next
    this.stepsTaken++
    this.lastStep = step
    this.settle()
    return next
  }

  /**
   * Start again: ignore some frames, then re-learn the cadence.
   *
   * Called after every tier change — a step rebuilds the scene, and the first frames of a
   * rebuilt scene are shader compilation, not rendering — and by anything else that invalidates
   * the picture, which in this product is the machine being reset and the GPU context coming
   * back. Re-learning the cadence as well as pausing is the point: a laptop that just switched
   * to its integrated GPU on battery is now a different display timing, and the old line would
   * be measuring the new machine against the old one's refresh rate.
   */
  settle(): void {
    this.settleLeft = SETTLE_FRAMES
    this.cadenceSamples = []
    this.cadence = 0
    this.lateSeconds = 0
    this.atFloor = false
  }

  /** Somebody else changed the tier — settings, or a fresh calibration. Adopt it and settle. */
  adopt(tier: RenderTier): void {
    if (tier === this.tierName) return
    this.tierName = tier
    this.settle()
  }

  /**
   * The median of the settling frames, clamped to something a display could actually be doing.
   *
   * Median rather than mean because one 200 ms hitch would drag a mean up by 4 ms, and rather
   * than minimum because animation-frame coalescing produces occasional near-zero intervals
   * that a minimum would adopt as the refresh rate and then measure everything against.
   */
  private learnCadence(): number {
    const sorted = [...this.cadenceSamples].sort((a, b) => a - b)
    const median = sorted[Math.floor(sorted.length / 2)] ?? 0
    return Math.min(median, MAX_CADENCE_MS)
  }
}
